import { z } from 'zod';
import { FILE_TYPES, KNOWLEDGE_SOURCE_TYPES, type FileType } from './entitlements';

export const KNOWLEDGE_VISIBILITY = ['agent', 'tenant'] as const;
export const DOCUMENT_STATUSES = [
  'uploaded',
  'scanning',
  'processing',
  'ready',
  'failed',
  'quarantined',
  'disabled',
] as const;
export const SOURCE_STATUSES = ['active', 'syncing', 'error', 'disabled'] as const;

/** Source types implemented so far; the rest are gated by plan and shown as coming next. */
export const AVAILABLE_SOURCE_TYPES = ['text', 'file', 'url', 'api'] as const;

/** How often a website source is re-crawled; the plan caps it (knowledge.url.refresh). */
export const URL_REFRESH = ['manual', 'weekly', 'daily'] as const;
export type UrlRefresh = (typeof URL_REFRESH)[number];

const REFRESH_RANK: Record<UrlRefresh | 'none', number> = { none: 0, manual: 0, weekly: 1, daily: 2 };

/** Whether a plan limit ('none' | 'weekly' | 'daily') allows the chosen refresh. */
export const refreshAllowed = (chosen: UrlRefresh, planLimit: 'none' | 'weekly' | 'daily') =>
  REFRESH_RANK[chosen] <= REFRESH_RANK[planLimit];

/** Minimum time between two manual "sync now" requests for the same source. */
export const MANUAL_SYNC_COOLDOWN_MINUTES = 60;

export const MIME_BY_FILE_TYPE: Record<FileType, string[]> = {
  pdf: ['application/pdf'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  csv: ['text/csv', 'application/csv', 'application/vnd.ms-excel'],
  txt: ['text/plain'],
  md: ['text/markdown', 'text/x-markdown', 'text/plain'],
};

/** Extension decides the type; the browser-reported MIME is only a hint and is normalized server-side. */
export function fileTypeFromName(name: string): FileType | null {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  return (FILE_TYPES as readonly string[]).includes(ext) ? (ext as FileType) : null;
}

const trimmed = (min: number, max: number, label: string) =>
  z
    .string()
    .trim()
    .min(min, `${label}: escribe al menos ${min} caracteres.`)
    .max(max, `${label}: máximo ${max} caracteres.`);

// WHATWG URL exists in browsers and Node; this package ships without DOM/Node typings.
declare const URL: new (input: string) => {
  protocol: string;
  hostname: string;
  username: string;
  password: string;
};

/** Public website only; the crawler re-validates every resolved address (SSRF guard). */
export const startUrlSchema = z
  .string()
  .trim()
  .max(2000, 'La dirección es demasiado larga.')
  .refine((v) => {
    try {
      const u = new URL(v);
      const host = u.hostname.toLowerCase();
      // Domain names only: IP literals and local names never point at a public website we should crawl.
      const ipLiteral = /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[');
      const local = host === 'localhost' || /\.(local|internal|localhost)$/.test(host);
      return (
        u.protocol === 'https:' && !u.username && !u.password && host.includes('.') && !ipLiteral && !local
      );
    } catch {
      return false;
    }
  }, 'Usa una dirección pública que empiece por https://');

const maxPagesSchema = z.number().int().min(1, 'Al menos 1 página.').max(10_000);

export const urlSourceConfigSchema = z.object({
  startUrl: startUrlSchema,
  maxPages: maxPagesSchema,
  refresh: z.enum(URL_REFRESH).default('manual'),
});

/** Changes to a website source: no defaults, so omitted fields keep their current value. */
export const updateUrlConfigSchema = z
  .object({ maxPages: maxPagesSchema, refresh: z.enum(URL_REFRESH) })
  .partial();

/* ---------------------------------------------------------------- API connectors */

/** Refresh options for API connectors, in hours (null = manual). The plan sets the minimum. */
export const API_REFRESH_HOURS = [6, 24, 168] as const;
export const API_AUTH_TYPES = ['none', 'header', 'bearer'] as const;
export const API_PAGINATION = ['none', 'page', 'next'] as const;

/** Dot path into a JSON item: "data.items", "fields.name", "tags.0". */
const jsonPath = z
  .string()
  .trim()
  .max(200)
  .regex(
    /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/,
    'Usa nombres de campo separados por puntos, p. ej. data.items.',
  );

const headerName = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{1,64}$/, 'Nombre de encabezado inválido.')
  .refine(
    (h) =>
      !['authorization', 'cookie', 'host', 'proxy-authorization', 'content-length'].includes(h.toLowerCase()),
    'Ese encabezado no se puede fijar aquí; usa la autenticación.',
  );

export const apiSourceConfigSchema = z
  .object({
    endpoint: startUrlSchema,
    auth: z.object({
      type: z.enum(API_AUTH_TYPES).default('none'),
      /** For type "header": e.g. X-API-Key. The value is stored as a secret, never here. */
      headerName: headerName.optional(),
    }),
    headers: z.record(headerName, z.string().max(500)).default({}),
    items: z.object({
      /** Where the list lives; empty when the response is the list itself. */
      path: z.union([jsonPath, z.literal('')]).default(''),
      id: jsonPath,
      title: jsonPath,
      text: z.array(jsonPath).min(1, 'Elige al menos un campo de texto.').max(20),
      link: jsonPath.optional(),
    }),
    pagination: z
      .object({
        type: z.enum(API_PAGINATION).default('none'),
        /** "page": query parameter that carries the page number (starts at 1). */
        param: z
          .string()
          .trim()
          .regex(/^[A-Za-z0-9_.-]{1,50}$/)
          .optional(),
        /** "next": path to the next page URL in the response (same host only). */
        nextPath: jsonPath.optional(),
        maxPages: z.number().int().min(1).max(100).default(10),
      })
      .default({ type: 'none', maxPages: 10 }),
    refreshHours: z
      .union([z.literal(6), z.literal(24), z.literal(168)])
      .nullable()
      .default(null),
  })
  .refine((c) => Object.keys(c.headers).length <= 5, { path: ['headers'], message: 'Máximo 5 encabezados.' })
  .refine((c) => c.auth.type !== 'header' || !!c.auth.headerName, {
    path: ['auth', 'headerName'],
    message: 'Indica el nombre del encabezado de la API key.',
  })
  .refine((c) => c.pagination.type !== 'page' || !!c.pagination.param, {
    path: ['pagination', 'param'],
    message: 'Indica el parámetro de página.',
  })
  .refine((c) => c.pagination.type !== 'next' || !!c.pagination.nextPath, {
    path: ['pagination', 'nextPath'],
    message: 'Indica dónde viene la siguiente página.',
  });

/** Write-only credential: sent on create/update, stored in the secret manager, never returned. */
const apiSecretSchema = z.string().trim().min(8, 'La credencial es muy corta.').max(4000);

/** Preview before saving: the connector is called once and the first items are shown mapped. */
export const previewApiSchema = z.object({
  api: apiSourceConfigSchema,
  apiSecret: apiSecretSchema.optional(),
  /** Existing source: reuse its stored credential when no new one is typed. */
  sourceId: z.uuid().optional(),
});

export const createKnowledgeSourceSchema = z
  .object({
    name: trimmed(2, 120, 'Nombre'),
    type: z.enum(KNOWLEDGE_SOURCE_TYPES),
    visibility: z.enum(KNOWLEDGE_VISIBILITY).default('agent'),
    url: urlSourceConfigSchema.optional(),
    api: apiSourceConfigSchema.optional(),
    apiSecret: apiSecretSchema.optional(),
  })
  .refine((v) => v.type !== 'url' || v.url, {
    path: ['url', 'startUrl'],
    message: 'Indica la dirección del sitio.',
  })
  .refine((v) => v.type !== 'api' || v.api, {
    path: ['api', 'endpoint'],
    message: 'Indica la dirección de la API.',
  })
  .refine((v) => v.type !== 'api' || !v.api || v.api.auth.type === 'none' || v.apiSecret, {
    path: ['apiSecret'],
    message: 'Escribe la credencial de la API.',
  });

export const updateKnowledgeSourceSchema = z
  .object({
    name: trimmed(2, 120, 'Nombre'),
    visibility: z.enum(KNOWLEDGE_VISIBILITY),
    url: updateUrlConfigSchema,
    /** Replaces the whole connector configuration (the form always sends all of it). */
    api: apiSourceConfigSchema,
    apiSecret: apiSecretSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'No hay cambios para guardar.' });

export const createTextDocumentSchema = z.object({
  title: trimmed(2, 200, 'Título'),
  content: trimmed(10, 200_000, 'Contenido'),
});

export const requestUploadSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  sizeBytes: z
    .number()
    .int()
    .min(1, 'El archivo está vacío.')
    .max(1024 * 1024 * 1024),
});

export type KnowledgeVisibility = (typeof KNOWLEDGE_VISIBILITY)[number];
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];
export type SourceStatus = (typeof SOURCE_STATUSES)[number];
export type CreateKnowledgeSourceInput = z.input<typeof createKnowledgeSourceSchema>;
export type UrlSourceConfig = z.infer<typeof urlSourceConfigSchema>;
export type ApiSourceConfig = z.infer<typeof apiSourceConfigSchema>;
export type ApiSourceConfigInput = z.input<typeof apiSourceConfigSchema>;
